import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { proBuilderKeys } from '~/lib/query-keys';
import { useProBuilderOpenStore } from '~/lib/stores/pro-builder-open-store';
import { useProBuilderEnabled } from '~/hooks/queries/use-feature-flags';

/**
 * Eddy Pro Builder (FRONT-3003) — wire shapes mirror `GET /api/pro-builder/status`
 * and the `PUT /api/pro-builder/connection` response. Secrets are never in
 * any response: only the `has*` booleans and the (non-secret) region.
 */
export type ProBuilderMode = 'subscription' | 'api_key' | 'bedrock';

export interface ProBuilderConnection {
  mode: ProBuilderMode;
  hasApiKey: boolean;
  hasBedrock: boolean;
  bedrockRegion: string | null;
}

export interface ProBuilderStatus {
  orgEnabled: boolean;
  canManageOrg: boolean;
  connection: ProBuilderConnection;
  /** Non-reversible tag of the credential a terminal spawned now would carry; null for none. */
  credentialTag: string | null;
  /** FRONT-3228: the saved auto-compact threshold (50…90 in steps of 5); null = Claude Code's default. Absent from an older server. */
  autoCompactPct?: number | null;
}

export const PRO_BUILDER_STATUS_PATH = '/api/pro-builder/status';

/**
 * The status read. `enabled` only while the per-person flag is on — with the
 * flag off every Pro Builder route answers the unknown-URL 404, so asking
 * would only produce a failed request for a feature the person cannot see.
 */
export function useProBuilderStatus() {
  const flagOn = useProBuilderEnabled();
  return useQuery({
    queryKey: proBuilderKeys.status(),
    queryFn: () => api.get<ProBuilderStatus>(PRO_BUILDER_STATUS_PATH),
    enabled: flagOn,
  });
}

/** One folder in the person's projects folder, and the app it publishes to (null = not published yet). */
export interface ProBuilderProject {
  name: string;
  /** The server's plain-English reason this folder cannot be published right now (name rule, archived app…); null = it can. */
  blockedReason: string | null;
  app: null | {
    slug: string;
    title: string;
    url: string;
    status: 'draft' | 'live' | 'archived';
    updatedAt: string;
  };
}

export const PRO_BUILDER_PROJECTS_PATH = '/api/pro-builder/projects';
export const PRO_BUILDER_PROJECTS_KEY = proBuilderKeys.projects();

/** The Publish dialog's list. `enabled` is the dialog being open, so nothing is read for a closed one. */
export function useProBuilderProjects(enabled: boolean) {
  return useQuery({
    queryKey: PRO_BUILDER_PROJECTS_KEY,
    queryFn: () => api.get<{ projects: ProBuilderProject[] }>(PRO_BUILDER_PROJECTS_PATH),
    enabled,
  });
}


export const PRO_BUILDER_APP_PATH = '/api/pro-builder/app';

/** `GET /api/pro-builder/app`: the project folder the window is bound to (null = none, or it no longer exists) and its project row. */
export interface ProBuilderBoundApp {
  folder: string | null;
  project: ProBuilderProject | null;
}

/**
 * Refetch on focus only while the window is OPEN: a closed window re-listed the
 * sandbox on every tab focus for nothing. Opening the terminal invalidates the
 * read, so it is fresh whenever the window is shown.
 */
export function proBuilderAppRefetchOnFocus(): 'always' | false {
  return useProBuilderOpenStore.getState().open ? 'always' : false;
}

/**
 * The window's binding read. `refetchOnWindowFocus: 'always'` (not just when
 * stale): a second tab can switch app, and this tab's header must name the
 * folder the terminal actually runs in before Publish is pressed.
 */
export function proBuilderAppQueryOptions(enabled: boolean) {
  return {
    queryKey: proBuilderKeys.app(),
    queryFn: () => api.get<ProBuilderBoundApp>(PRO_BUILDER_APP_PATH),
    enabled,
    refetchOnWindowFocus: proBuilderAppRefetchOnFocus,
  };
}

/** The window's binding (FRONT-3077). `enabled` is "the organization switch is on and the window needs it". */
export function useProBuilderApp(enabled: boolean) {
  return useQuery(proBuilderAppQueryOptions(enabled));
}
